import { GridApi } from '@mui/x-data-grid-pro';
import { MutableRefObject, useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import { DateRangePickerCell, DateRangePickerChangeEvent } from '../DateRangePickerCell/DateRangePickerCell';
import { I18nNamespace } from '~/common/types';
import { UserProfileAbsenceDataGridField } from '~/common/types/UserProfileAbsenceDataGrid';
import { DataGridConfig, DateRangeCell, cellFactory, gridColDefFactory } from '~/components/DataGridCustom';

import { AbsenceGridRow } from './UserProfileAbsenceGridForm.types';

export type UseUserProfleAbsenceDataGridFormConfigProps = {
  editable: boolean;
  apiRef: MutableRefObject<GridApi>;
};

export type UseUserProfleAbsenceDataGridFormConfigResult = {
  config: DataGridConfig<AbsenceGridRow>;
};

export type UseUserProfleAbsenceDataGridFormConfig = ({
  apiRef,
}: UseUserProfleAbsenceDataGridFormConfigProps) => UseUserProfleAbsenceDataGridFormConfigResult;

export const useUserProfleAbsenceDataGridFormConfig: UseUserProfleAbsenceDataGridFormConfig = ({
  apiRef,
  editable,
}) => {
  const { t } = useTranslation();
  const { t: tColumn } = useTranslation(I18nNamespace.widgets, {
    keyPrefix: 'UserProfileAbsenceWidget.datagrid.column',
  });

  return useMemo(() => {
    const createColumn = gridColDefFactory({ tColumn, t, defaultOptions: { sortable: false } });

    const onValueChange = ({ value, row, field }: DateRangePickerChangeEvent) => {
      const [from, to] = value;
      apiRef.current.setEditCellValue({
        id: row.id,
        field,
        value: { from, to },
      });
    };

    return {
      config: {
        columns: [
          createColumn({
            field: UserProfileAbsenceDataGridField.ABSENT_TIME,
            hideable: false,
            resizable: false,
            editable,
            flex: 1,
            renderCell: cellFactory(DateRangeCell),
            renderEditCell: ({ row, field, value }) => (
              <DateRangePickerCell onValueChange={onValueChange} row={row} field={field} value={value} />
            ),
          }),
        ],
      },
    } as UseUserProfleAbsenceDataGridFormConfigResult;
  }, [tColumn, t, apiRef, editable]);
};
